import { authService } from "@/api/auth-service";
import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const forgotPasswordSchema = z.object({
	email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

interface ForgotPasswordModalProps {
	onSuccess?: () => void;
}

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ onSuccess }) => {
	const { closeModal } = useModalControl();
	const [isLoading, setIsLoading] = useState(false);
	const [emailSent, setEmailSent] = useState(false);

	const form = useForm<ForgotPasswordFormData>({
		resolver: zodResolver(forgotPasswordSchema),
		defaultValues: {
			email: "",
		},
	});

	const handleSubmit = async (values: ForgotPasswordFormData) => {
		setIsLoading(true);

		try {
			await authService.forgotPassword(values.email);
			setEmailSent(true);
			toast.success("Password reset link sent to your email!");
			onSuccess?.();
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const errorWithResponse = error as { response?: { status?: number; data?: { errors?: Record<string, string[]> } } };
				if (errorWithResponse.response?.status === 422) {
					const serverErrors = errorWithResponse.response.data?.errors || {};
					for (const key of Object.keys(serverErrors)) {
						form.setError(key as keyof ForgotPasswordFormData, {
							message: serverErrors[key][0],
						});
					}
				} else {
					toast.error("Failed to send password reset email. Please try again.");
				}
			} else {
				toast.error("Failed to send password reset email. Please try again.");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		form.reset();
		setEmailSent(false);
		closeModal();
	};

	return (
		<div className="flex flex-col w-full gap-6">
			{emailSent ? (
				<div className="text-center">
					<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
						<svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" role="img" aria-label="Success checkmark">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
						</svg>
					</div>
					<p className="text-gray-600 mb-4">
						We've sent a password reset link to <strong>{form.getValues("email")}</strong>.
					</p>
					<p className="text-sm text-gray-500 mb-6">Please check your email and click the link to reset your password. The link will expire in 60 minutes.</p>
					<Button onClick={handleClose} className="w-full">
						Close
					</Button>
				</div>
			) : (
				<Form {...form}>
					<form id="forgot-password-form" className="flex flex-col w-full gap-6" onSubmit={form.handleSubmit(handleSubmit)}>
						<div className="flex flex-col w-full gap-4">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Email Address</FormLabel>
										<FormGroup>
											<Input type="email" placeholder="Enter your email address" {...field} disabled={isLoading} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="flex gap-3 mt-2">
								<Button type="button" variant="outline" onClick={handleClose} className="flex-1" disabled={isLoading}>
									Cancel
								</Button>
								<Button type="submit" className="flex-1" loading={isLoading} disabled={isLoading}>
									Send Reset Link
								</Button>
							</div>
						</div>
					</form>
				</Form>
			)}
		</div>
	);
};
